import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DoNotDisturbOnRoundedIcon from "@mui/icons-material/DoNotDisturbOnRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  Box,
  Button,
  Paper,
  Stack,
  useTheme,
  Typography,
  CircularProgress,
  alpha,
  Container,
  Grid,
} from "@mui/material";
import { addDays, format, isValid, startOfDay, startOfToday } from "date-fns";
import { enGB, fr } from "date-fns/locale";
import FadeInUp from "bundles/common/utils/FadeInUp";
import sh from "bundles/common/utils/sh";
import { getDateRangeValidation } from "bundles/common/utils/utils";
import ContactForm from "bundles/Main/components/ContactForm";

const PACKAGE_INCLUDES = [
  "Accommodation (with security)",
  "Daily breakfast and dinner",
  "House cleaning every day",
  "Airport pick-up and drop-off (from/to Arusha or Kilimanjaro airport)",
  "An orientation day to acquaint you with the local culture and your new surroundings",
  "Hospital placement assistance, aligning with your choice of department (please note that additional fees are paid directly to your placement as per their guidelines)",
  "A pair of personalised scrubs for healthcare interns and volunteers",
  "A T-shirt for non-medical volunteers",
];

const PACKAGE_EXCLUDES = [
  "Flights",
  "Travel Insurance",
  "Visa-related costs",
  "Hospital and Supervision fees",
  "Weekend trips and tours",
  "Snacks, softs drinks, gifts and souvenirs",
];

const pageBackground = (theme) =>
  `linear-gradient(165deg, ${alpha(theme.palette.secondary.main, 0.06)} 0%, #f8fafc 22%, #f1f5f9 55%, #eef2f7 100%)`;

const toIsoDate = (d) => (d && isValid(d) ? format(d, "yyyy-MM-dd") : "");

const PackageCard = ({ title, items, variant, theme }) => {
  const isInclude = variant === "include";
  const accent = theme.palette.secondary.main;
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        p: { xs: 2.5, md: 3.5 },
        borderRadius: 3,
        border: `1px solid ${alpha(accent, 0.12)}`,
        background: isInclude
          ? `linear-gradient(160deg, ${alpha(accent, 0.07)} 0%, #fff 48%)`
          : alpha(theme.palette.common.black, 0.02),
        boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2.5 }}>
        <Box
          sx={{
            width: 8,
            height: 32,
            borderRadius: 1,
            bgcolor: isInclude ? accent : alpha(theme.palette.common.black, 0.2),
          }}
        />
        <Typography
          variant="overline"
          sx={{
            letterSpacing: "0.14em",
            fontWeight: 700,
            color: accent,
            lineHeight: 1.2,
            fontSize: { xs: "0.8125rem", sm: "0.75rem" },
          }}
        >
          {title}
        </Typography>
      </Stack>
      <Stack component="ul" spacing={1.75} sx={{ m: 0, p: 0, listStyle: "none" }}>
        {items.map((line) => (
          <Stack
            key={line}
            component="li"
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            sx={{ "& svg": { flexShrink: 0, mt: { xs: 0.45, sm: 0.35 } } }}
          >
            {isInclude ? (
              <CheckCircleRoundedIcon sx={{ fontSize: { xs: 26, sm: 24, md: 22 }, color: accent }} />
            ) : (
              <DoNotDisturbOnRoundedIcon sx={{ fontSize: { xs: 26, sm: 24, md: 22 }, color: alpha(theme.palette.common.black, 0.35) }} />
            )}
            <Typography
              variant="body1"
              sx={{
                color: "text.primary",
                lineHeight: 1.6,
                fontSize: { xs: "1.0625rem", sm: "1.03125rem", md: "1rem" },
              }}
            >
              {line}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

const Pricing = () => {
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const navigate = useNavigate();
  const theme = useTheme();
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  // enGB / fr: Monday as first day of week (European calendar)
  const dateFnsLocale = i18nLocale === "fr" ? fr : enGB;
  const [totalPrice, setTotalPrice] = useState(null);
  const [stayDays, setStayDays] = useState(null);
  const [dateError, setDateError] = useState(null);
  const [loadingPrice, setLoadingPrice] = useState(false);

  const accent = theme.palette.secondary.main;

  const fetchPrice = useCallback(() => {
    const fromStr = toIsoDate(fromDate);
    const toStr = toIsoDate(toDate);
    const { valid, message } = getDateRangeValidation(fromStr, toStr);
    if (!fromStr || !toStr) {
      setTotalPrice(null);
      setStayDays(null);
      setDateError(null);
      return;
    }
    if (!valid) {
      setTotalPrice(null);
      setStayDays(null);
      setDateError(message);
      return;
    }

    setDateError(null);
    setLoadingPrice(true);
    sh.post("/calculate_price", {
      authenticity_token: authenticityToken,
      from_date: fromStr,
      to_date: toStr,
    })
      .then((response) => {
        setTotalPrice(response.data.totalPrice);
        setStayDays(response.data.numberOfDays);
      })
      .catch(() => {
        setTotalPrice(null);
        setStayDays(null);
        setDateError("We could not calculate the price. Please try again.");
      })
      .finally(() => {
        setLoadingPrice(false);
      });
  }, [fromDate, toDate, authenticityToken]);

  useEffect(() => {
    const t = setTimeout(fetchPrice, 350);
    return () => clearTimeout(t);
  }, [fetchPrice]);

  const goToApply = () => {
    if (totalPrice == null || dateError) return;
    navigate(
      `/${i18nLocale}/apply?start_date=${toIsoDate(fromDate)}&end_date=${toIsoDate(toDate)}`
    );
  };

  const pickerSlotProps = {
    textField: {
      fullWidth: true,
      sx: {
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          bgcolor: "background.paper",
          fontSize: { xs: "1.0625rem", sm: "1rem" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: accent,
            borderWidth: 2,
          },
        },
        "& .MuiInputLabel-root": {
          fontSize: { xs: "1rem", sm: "0.875rem" },
          "&.Mui-focused": {
            color: accent,
          },
        },
        "& .MuiOutlinedInput-input": {
          fontSize: { xs: "1.0625rem", sm: "1rem" },
          py: { xs: 1.5, sm: 1.25 },
        },
        "& .MuiIconButton-root": {
          color: alpha(accent, 0.65),
          "&:hover": {
            color: accent,
            bgcolor: alpha(accent, 0.08),
          },
        },
      },
    },
    desktopPaper: {
      sx: {
        borderRadius: 3,
        boxShadow: "0 20px 48px rgba(15, 23, 42, 0.14)",
        border: `1px solid ${alpha(theme.palette.secondary.main, 0.12)}`,
      },
    },
    mobilePaper: {
      sx: {
        borderRadius: 3,
      },
    },
    layout: {
      sx: {
        "& .MuiPickersCalendarHeader-root": {
          paddingLeft: 1,
          paddingRight: 1,
          "& .MuiPickersCalendarHeader-labelContainer": {
            fontSize: { xs: "1.0625rem", sm: "1rem" },
          },
          "& .MuiIconButton-root": {
            color: accent,
            "&:hover": {
              bgcolor: alpha(accent, 0.08),
            },
          },
        },
        "& .MuiPickersDay-root": {
          fontSize: { xs: "0.9375rem", sm: "0.875rem" },
          "&.Mui-selected": {
            backgroundColor: `${accent} !important`,
            color: `${theme.palette.secondary.contrastText} !important`,
            "&:hover, &:focus": {
              backgroundColor: `${alpha(accent, 0.88)} !important`,
            },
          },
          "&.MuiPickersDay-today:not(.Mui-selected)": {
            borderColor: accent,
          },
        },
        "& .MuiPickersYear-yearButton.Mui-selected, & .MuiPickersMonth-monthButton.Mui-selected": {
          backgroundColor: `${accent} !important`,
          color: `${theme.palette.secondary.contrastText} !important`,
          "&:hover": {
            backgroundColor: `${alpha(accent, 0.88)} !important`,
          },
        },
        "& .MuiDayCalendar-weekContainer": {
          margin: 0,
        },
      },
    },
    actionBar: {
      sx: {
        "& .MuiButton-root": {
          color: accent,
        },
        "& .MuiButton-root.MuiButton-contained": {
          bgcolor: accent,
          color: theme.palette.secondary.contrastText,
          "&:hover": {
            bgcolor: alpha(accent, 0.9),
          },
        },
      },
    },
  };

  const handleFromChange = (next) => {
    setFromDate(next);
    if (next && toDate && isValid(next) && isValid(toDate)) {
      if (startOfDay(toDate) <= startOfDay(next)) {
        setToDate(null);
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateFnsLocale}>
      <Box
        sx={{
          width: "100%",
          minHeight: "100%",
          background: pageBackground(theme),
          pb: { xs: 2, md: 3 },
        }}
      >
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <FadeInUp>
            <Box
              sx={{
                pt: { xs: 3, md: 5 },
                pb: { xs: 3, md: 5 },
                textAlign: { xs: "left", md: "left" },
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  letterSpacing: "0.2em",
                  fontWeight: 700,
                  color: alpha(accent, 0.85),
                  display: "block",
                  mb: 1.5,
                  fontSize: { xs: "0.8125rem", sm: "0.75rem" },
                }}
              >
              Pricing
              </Typography>
              <Typography
                component="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "2.5rem", sm: "2.85rem", md: "3.25rem" },
                  lineHeight: { xs: 1.12, md: 1.08 },
                  letterSpacing: "-0.03em",
                  color: theme.palette.text.primary,
                  maxWidth: 720,
                }}
              >
              What your stay{" "}
                <Box
                  component="span"
                  sx={{
                    color: accent,
                    fontStyle: "italic",
                    fontWeight: 800,
                  }}
                >
                includes
                </Box>
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mt: 2,
                  maxWidth: 560,
                  color: "text.secondary",
                  lineHeight: 1.65,
                  fontSize: { xs: "1.125rem", sm: "1.0625rem", md: "1.05rem" },
                }}
              >
              One clear package for volunteers and interns. Pick your dates below for an instant total.
              </Typography>
            </Box>
          </FadeInUp>

          <FadeInUp>
            <Grid container spacing={{ xs: 2.5, md: 3 }} sx={{ mb: { xs: 4, md: 6 } }}>
              <Grid item xs={12} md={6}>
                <PackageCard title="Included in your package" items={PACKAGE_INCLUDES} variant="include" theme={theme} />
              </Grid>
              <Grid item xs={12} md={6}>
                <PackageCard title="Not included" items={PACKAGE_EXCLUDES} variant="exclude" theme={theme} />
              </Grid>
            </Grid>
          </FadeInUp>

          <FadeInUp>
            <Paper
              elevation={0}
              sx={{
                overflow: "hidden",
                borderRadius: { xs: 3, md: 4 },
                border: `1px solid ${alpha(accent, 0.1)}`,
                boxShadow: `0 20px 56px ${alpha(theme.palette.common.black, 0.08)}`,
                mb: { xs: 4, md: 6 },
              }}
            >
              <Grid container>
                <Grid
                  item
                  xs={12}
                  md={5}
                  sx={{
                    p: { xs: 3, sm: 4 },
                    background: `linear-gradient(145deg, ${alpha(accent, 0.1)} 0%, ${alpha(accent, 0.03)} 100%)`,
                    borderRight: { md: `1px solid ${alpha(accent, 0.1)}` },
                    borderBottom: { xs: `1px solid ${alpha(accent, 0.1)}`, md: "none" },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                    <CalendarMonthRoundedIcon sx={{ color: accent, fontSize: { xs: 32, sm: 30, md: 28 } }} />
                    <Typography
                      variant="overline"
                      sx={{
                        letterSpacing: "0.12em",
                        fontWeight: 700,
                        color: accent,
                        fontSize: { xs: "0.8125rem", sm: "0.75rem" },
                      }}
                    >
                    Your dates
                    </Typography>
                  </Stack>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      mb: 1,
                      letterSpacing: "-0.02em",
                      fontSize: { xs: "1.375rem", sm: "1.5rem" },
                    }}
                  >
                  Calculate your price
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3, lineHeight: 1.65, fontSize: { xs: "1.0625rem", sm: "1rem", md: "0.875rem" } }}
                  >
                  Arrival and departure update your total instantly. Minimum stay: 14 days.
                  </Typography>
                  <Stack spacing={2}>
                    <DatePicker
                      label="Arrival"
                      value={fromDate}
                      onChange={handleFromChange}
                      minDate={startOfToday()}
                      format="dd/MM/yyyy"
                      slotProps={{
                        ...pickerSlotProps,
                        field: { clearable: true },
                      }}
                    />
                    <DatePicker
                      label="Departure"
                      value={toDate}
                      onChange={setToDate}
                      minDate={
                        fromDate && isValid(fromDate) ? addDays(fromDate, 1) : startOfToday()
                      }
                      format="dd/MM/yyyy"
                      slotProps={{
                        ...pickerSlotProps,
                        field: { clearable: true },
                      }}
                    />
                  </Stack>
                  {dateError && (
                    <Typography
                      variant="body2"
                      color="error"
                      sx={{ mt: 2, lineHeight: 1.5, fontSize: { xs: "1.0625rem", sm: "1rem" } }}
                    >
                      {dateError}
                    </Typography>
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={7}
                  sx={{
                    p: { xs: 3, sm: 4, md: 5 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: { md: 320 },
                    bgcolor: "#fff",
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 400,
                      minHeight: 200,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 3,
                      px: 3,
                      py: 4,
                      bgcolor: alpha(accent, 0.04),
                      border: `1px solid ${alpha(accent, 0.12)}`,
                    }}
                  >
                    {loadingPrice ? (
                      <CircularProgress size={40} thickness={3.5} sx={{ color: accent }} />
                    ) : dateError ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: "1.0625rem", sm: "1rem" } }}>
                      —
                      </Typography>
                    ) : totalPrice != null ? (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1, fontWeight: 500, fontSize: { xs: "1.0625rem", sm: "1rem" } }}
                        >
                        Total for your stay
                          {stayDays != null ? ` · ${stayDays} days` : ""}
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: { xs: "3.125rem", sm: "3.25rem", md: "3.25rem" },
                            letterSpacing: "-0.03em",
                            color: accent,
                            lineHeight: 1,
                          }}
                        >
                          {Math.round(totalPrice)}€
                        </Typography>
                        <Button
                          variant="contained"
                          size="large"
                          onClick={goToApply}
                          sx={{
                            mt: 3,
                            borderRadius: 2,
                            px: { xs: 3, sm: 4 },
                            py: { xs: 1.5, sm: 1.25 },
                            minHeight: { xs: 48, sm: 42 },
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: { xs: "1.0625rem", sm: "1rem" },
                            boxShadow: `0 8px 24px ${alpha(accent, 0.35)}`,
                            bgcolor: accent,
                            "&:hover": { bgcolor: alpha(accent, 0.9) },
                          }}
                        >
                        Continue to application
                        </Button>
                      </>
                    ) : (
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        textAlign="center"
                        sx={{
                          lineHeight: 1.65,
                          maxWidth: 300,
                          fontSize: { xs: "1.0625rem", sm: "1rem" },
                        }}
                      >
                      Select your arrival and departure to see your total.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </FadeInUp>
        </Container>

        <ContactForm/>
      </Box>
    </LocalizationProvider>
  );
};

export default Pricing;
